// client/src/pages/admin/project-steps/Step1BasicInfo.tsx
import React, { useRef, useState, useEffect } from 'react';
import { StepProps } from './types';
import { SUBCATEGORIES } from './constants';

interface Step1Props extends StepProps {
    featuredImage?: File | null;
    setFeaturedImage?: React.Dispatch<React.SetStateAction<File | null>>;
    featuredPreview?: string;
    setFeaturedPreview?: React.Dispatch<React.SetStateAction<string>>;
}

const Step1BasicInfo: React.FC<Step1Props> = ({ 
    form, 
    setForm, 
    handleChange, 
    errors,
    featuredImage,
    setFeaturedImage,
    featuredPreview,
    setFeaturedPreview
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const ptRef = useRef<HTMLDivElement>(null);
    const scRef = useRef<HTMLDivElement>(null);

    // Dropdown open states
    const [ptOpen, setPtOpen] = useState(false);
    const [scOpen, setScOpen] = useState(false);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ptRef.current && !ptRef.current.contains(e.target as Node)) setPtOpen(false);
            if (scRef.current && !scRef.current.contains(e.target as Node)) setScOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Handle featured image change from Preview card
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && setFeaturedImage && setFeaturedPreview) {
            setFeaturedImage(file);
            setFeaturedPreview(URL.createObjectURL(file));
        }
    };

    // Trigger file select
    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    // Map Featured Category selector to form state
    const handleFeaturedCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        if (setForm) {
            setForm(prev => ({
                ...prev,
                isOurPick: val === 'Our Pick'
            }));
        }
    };

    // Toggle a property type in the multi-select array
    const togglePropertyType = (type: string) => {
        if (!setForm) return;
        setForm(prev => {
            const current = prev.propertyType || [];
            const isSelected = current.includes(type);
            const updated = isSelected
                ? current.filter(t => t !== type)
                : [...current, type];
            // Remove sub-categories that no longer belong to selected types
            const validSubs = updated.flatMap(t => SUBCATEGORIES[t] || []);
            const filteredSubs = prev.subCategory.filter(s => validSubs.includes(s));
            return { ...prev, propertyType: updated, subCategory: filteredSubs };
        });
    };

    // Toggle a sub-category in the multi-select array
    const toggleSubCategory = (sub: string) => {
        if (!setForm) return;
        setForm(prev => {
            const current = prev.subCategory || [];
            const isSelected = current.includes(sub);
            const updated = isSelected
                ? current.filter(s => s !== sub)
                : [...current, sub];
            return { ...prev, subCategory: updated };
        });
    };

    // All available sub-categories from all selected property types (deduplicated)
    const availableSubs = Array.from(
        new Set((form.propertyType || []).flatMap(t => SUBCATEGORIES[t] || []))
    );

    const propertyTypeDisplay =
        form.propertyType.length > 0
            ? form.propertyType.join(', ')
            : 'Select Property Type';

    const subCategoryDisplay =
        form.subCategory.length > 0
            ? form.subCategory.join(', ')
            : 'Select Sub Category';

    // Legacy compat
    const propertyTypeValue = form.propertyType[0] || '';

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-4 px-2">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                    1
                </div>
                <div>
                    <h5 className="text-xl font-bold text-gray-800">Basic Details</h5>
                </div>
            </div>

            {/* Hidden File Input for Preview Card Change Image */}
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={handleImageChange} 
            />

            {/* Inputs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-2 gap-y-4">
                
                {/* Project Name */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Project Name <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-gray-400">
                            <i className="fa-solid fa-city"></i>
                        </span>
                        <input
                            type="text"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.name ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Enter Project Name"
                            autoComplete="off"
                        />
                    </div>
                </div>

                {/* URL Slug */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">URL Slug <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-gray-400">
                            <i className="fa-solid fa-link"></i>
                        </span>
                        <input
                            type="text"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.slug ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="slug"
                            value={form.slug}
                            onChange={handleChange}
                            placeholder="e.g. the-blue-corner-jagatpur"
                            autoComplete="off"
                        />
                    </div>
                </div>

                {/* Listing Type */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Listing Type <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-emerald-500">
                            <i className="fa-solid fa-tag"></i>
                        </span>
                        <select
                            className={`w-full pl-10 pr-8 py-3 rounded-xl bg-gray-50 border ${errors?.listingType ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm appearance-none`}
                            name="listingType"
                            value={form.listingType}
                            onChange={handleChange}
                        >
                            <option value="">Select Listing Type</option>
                            <option value="For Sale">For Sale</option>
                            <option value="For Rent">For Rent</option>
                            <option value="Both">Both</option>
                        </select>
                        <span className="absolute right-4 text-gray-400 pointer-events-none">
                            <i className="fa-solid fa-chevron-down text-xs"></i>
                        </span>
                    </div>
                </div>


                {/* Property Type — Multi-select Checkbox Dropdown */}
                <div className="space-y-2 col-span-1 md:col-span-1" ref={ptRef}>
                    <label className="text-xs font-bold text-gray-600 block">Property Type <span className="text-rose-500">*</span></label>
                    <div className="relative">
                        {/* Trigger Button */}
                        <button
                            type="button"
                            onClick={() => { setPtOpen(o => !o); setScOpen(false); }}
                            className={`w-full flex items-center pl-10 pr-8 py-3 rounded-xl bg-gray-50 border text-left font-semibold text-sm transition-all ${
                                errors?.propertyType
                                    ? 'border-rose-400 bg-rose-50 text-rose-500'
                                    : ptOpen
                                        ? 'border-blue-500 bg-white text-gray-800'
                                        : 'border-gray-200 text-gray-700'
                            }`}
                        >
                            <span className="absolute left-4 text-indigo-500">
                                <i className="fa-solid fa-house text-sm"></i>
                            </span>
                            <span className="truncate flex-1">
                                {form.propertyType.length === 0
                                    ? <span className="text-gray-400">Select Property Type</span>
                                    : propertyTypeDisplay
                                }
                            </span>
                            {form.propertyType.length > 0 && (
                                <span className="mx-1 min-w-[18px] h-[18px] rounded-full bg-indigo-500 text-white text-[10px] font-black flex items-center justify-center">
                                    {form.propertyType.length}
                                </span>
                            )}
                            <span className="absolute right-3 text-gray-400">
                                <i className={`fa-solid fa-chevron-${ptOpen ? 'up' : 'down'} text-xs`}></i>
                            </span>
                        </button>

                        {/* Dropdown Panel */}
                        {ptOpen && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                <div className="p-1.5 max-h-52 overflow-y-auto">
                                    {Object.keys(SUBCATEGORIES).map(type => {
                                        const checked = form.propertyType.includes(type);
                                        return (
                                            <label
                                                key={type}
                                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                                                    checked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={checked}
                                                    onChange={() => togglePropertyType(type)}
                                                />
                                                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                                    checked
                                                        ? 'bg-indigo-500 border-indigo-500'
                                                        : 'border-gray-300 bg-white'
                                                }`}>
                                                    {checked && <i className="fa-solid fa-check text-white" style={{fontSize:'8px'}}></i>}
                                                </span>
                                                <span className={`text-xs font-bold ${
                                                    checked ? 'text-indigo-700' : 'text-gray-700'
                                                }`}>{type}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {form.propertyType.length > 0 && (
                                    <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50">
                                        <span className="text-[10px] font-bold text-indigo-600">{form.propertyType.length} selected</span>
                                        <button
                                            type="button"
                                            onClick={() => setForm && setForm(prev => ({ ...prev, propertyType: [], subCategory: [] }))}
                                            className="text-[10px] font-bold text-rose-500 hover:text-rose-700"
                                        >
                                            Clear all
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sub Category — Multi-select Checkbox Dropdown */}
                <div className="space-y-2 col-span-1 md:col-span-1" ref={scRef}>
                    <label className="text-xs font-bold text-gray-600 block">Sub Category <span className="text-rose-500">*</span></label>
                    <div className="relative">
                        {/* Trigger Button */}
                        <button
                            type="button"
                            disabled={form.propertyType.length === 0}
                            onClick={() => { setScOpen(o => !o); setPtOpen(false); }}
                            className={`w-full flex items-center pl-10 pr-8 py-3 rounded-xl bg-gray-50 border text-left font-semibold text-sm transition-all ${
                                form.propertyType.length === 0
                                    ? 'border-gray-100 text-gray-300 cursor-not-allowed opacity-60'
                                    : errors?.subCategory
                                        ? 'border-rose-400 bg-rose-50 text-rose-500'
                                        : scOpen
                                            ? 'border-blue-500 bg-white text-gray-800'
                                            : 'border-gray-200 text-gray-700'
                            }`}
                        >
                            <span className="absolute left-4 text-amber-500">
                                <i className="fa-solid fa-table-cells-large text-sm"></i>
                            </span>
                            <span className="truncate flex-1">
                                {form.subCategory.length === 0
                                    ? <span className="text-gray-400">Select Sub Category</span>
                                    : subCategoryDisplay
                                }
                            </span>
                            {form.subCategory.length > 0 && (
                                <span className="mx-1 min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-black flex items-center justify-center">
                                    {form.subCategory.length}
                                </span>
                            )}
                            <span className="absolute right-3 text-gray-400">
                                <i className={`fa-solid fa-chevron-${scOpen ? 'up' : 'down'} text-xs`}></i>
                            </span>
                        </button>

                        {/* Dropdown Panel */}
                        {scOpen && availableSubs.length > 0 && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                <div className="p-1.5 max-h-52 overflow-y-auto">
                                    {availableSubs.map(sub => {
                                        const checked = form.subCategory.includes(sub);
                                        return (
                                            <label
                                                key={sub}
                                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                                                    checked ? 'bg-amber-50' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={checked}
                                                    onChange={() => toggleSubCategory(sub)}
                                                />
                                                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                                    checked
                                                        ? 'bg-amber-500 border-amber-500'
                                                        : 'border-gray-300 bg-white'
                                                }`}>
                                                    {checked && <i className="fa-solid fa-check text-white" style={{fontSize:'8px'}}></i>}
                                                </span>
                                                <span className={`text-xs font-bold ${
                                                    checked ? 'text-amber-700' : 'text-gray-700'
                                                }`}>{sub}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {form.subCategory.length > 0 && (
                                    <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50">
                                        <span className="text-[10px] font-bold text-amber-600">{form.subCategory.length} selected</span>
                                        <button
                                            type="button"
                                            onClick={() => setForm && setForm(prev => ({ ...prev, subCategory: [] }))}
                                            className="text-[10px] font-bold text-rose-500 hover:text-rose-700"
                                        >
                                            Clear all
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Configuration */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Configuration <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-pink-500">
                            <i className="fa-solid fa-building-user"></i>
                        </span>
                        <input
                            type="text"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.overviewConfiguration ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="overviewConfiguration"
                            value={form.overviewConfiguration}
                            onChange={handleChange}
                            placeholder="e.g. 3 BHK, 3 & 4 BHK, 2 & 3 BHK"
                            autoComplete="off"
                        />
                    </div>
                </div>

                {/* Starting Price */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Starting Price <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-emerald-600 font-bold text-base">
                            ₹
                        </span>
                        <input
                            type="text"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.overviewPriceRange ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="overviewPriceRange"
                            value={form.overviewPriceRange}
                            onChange={handleChange}
                            placeholder="e.g. ₹1.24 Cr onwards"
                            autoComplete="off"
                        />
                    </div>
                </div>


                {/* RERA Approved */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">RERA Approved <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-emerald-500">
                            <i className="fa-solid fa-shield-check"></i>
                        </span>
                        <select
                            className={`w-full pl-10 pr-8 py-3 rounded-xl bg-gray-50 border ${errors?.reraApproved ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm appearance-none`}
                            name="reraApproved"
                            value={form.reraApproved || 'Yes'}
                            onChange={handleChange}
                        >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                        <span className="absolute right-4 text-gray-400 pointer-events-none">
                            <i className="fa-solid fa-chevron-down text-xs"></i>
                        </span>
                    </div>
                </div>

                {/* Project Launching Date */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Project Launching Date <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-blue-500">
                            <i className="fa-solid fa-rocket"></i>
                        </span>
                        <input
                            type="date"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.launchDate ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="launchDate"
                            value={form.launchDate ? new Date(form.launchDate).toISOString().split('T')[0] : ''}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Possession Date */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Posession Date <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-blue-500">
                            <i className="fa-solid fa-calendar-days"></i>
                        </span>
                        <input
                            type="date"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.possessionDate ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="possessionDate"
                            value={form.possessionDate ? new Date(form.possessionDate).toISOString().split('T')[0] : ''}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* RERA Registration No. */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">RERA Registration No.</label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-blue-500">
                            <i className="fa-solid fa-shield-halved"></i>
                        </span>
                        <input
                            type="text"
                            className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 border ${errors?.reraNumber ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="reraNumber"
                            value={form.reraNumber}
                            onChange={handleChange}
                            placeholder="e.g. PR/GJ/AHMEDABAD/..."
                            disabled={form.reraApproved === 'No'}
                            autoComplete="off"
                        />
                    </div>
                </div>

                {/* Project Status */}
                <div className="space-y-2 col-span-1 md:col-span-1">
                    <label className="text-xs font-bold text-gray-600 block">Project Status <span className="text-rose-500">*</span></label>
                    <div className="relative flex items-center">
                        <span className="absolute left-4 text-indigo-500">
                            <i className="fa-solid fa-hourglass-half"></i>
                        </span>
                        <select
                            className={`w-full pl-10 pr-8 py-3 rounded-xl bg-gray-50 border ${errors?.projectStatus ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-gray-200 text-gray-800'} focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm appearance-none`}
                            name="projectStatus"
                            value={form.projectStatus}
                            onChange={handleChange}
                        >
                            <option value="">Select Project Status</option>
                            <option value="Under Construction">Under Construction</option>
                            <option value="Ready to Move">Ready to Move</option>
                            <option value="Newly Launched">Newly Launched</option>
                        </select>
                        <span className="absolute right-4 text-gray-400 pointer-events-none">
                            <i className="fa-solid fa-chevron-down text-xs"></i>
                        </span>
                    </div>
                </div>

            </div>

            {/* Configuration Info Box */}
            <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl p-4 text-blue-700 text-xs font-semibold shadow-sm">
                <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-info text-blue-500 text-sm"></i>
                    <span>Enter short & smart configuration (e.g. 3 BHK, 3 & 4 BHK, Office & Showroom, 3 BHK + Retail Shops)</span>
                </div>
                <button type="button" className="text-blue-500 hover:text-blue-700 font-bold">
                    <i className="fa-solid fa-xmark text-sm"></i>
                </button>
            </div>

            {/* Tower & Block Details */}
            <div className="space-y-4 pt-6 border-t border-gray-100">
                <h6 className="text-sm font-bold text-[#1e1b4b]">Tower & Block Details</h6>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    {/* Towers / Blocks */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-city text-purple-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Towers / Blocks <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewTotalTowers" 
                                value={form.overviewTotalTowers || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="6" 
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    {/* Total Floors */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                            <i className="fa-regular fa-building text-blue-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Total Floors <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewTotalFloors" 
                                value={form.overviewTotalFloors || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="14" 
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    {/* Sub-G + Floors */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-house-chimney-window text-blue-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Sub-G + Floors <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewFloors" 
                                value={form.overviewFloors || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="Basement + G + 14" 
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    {/* Total Units */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-building-user text-purple-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Total Units <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewTotalUnits" 
                                value={form.overviewTotalUnits || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="420" 
                                autoComplete="off"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Live Preview Section */}
            <div className="space-y-3 mt-6">
                <h6 className="text-sm font-bold text-gray-700">Preview (How it will appear on website)</h6>
                
                {/* Horizontal Preview Card */}
                <div className="flex flex-col md:flex-row bg-green-50 border border-gray-100 rounded-2xl overflow-hidden shadow-md w-full">
                    
                    {/* Left: Card Image */}
                    <div className="relative w-full md:w-80 h-48 md:h-auto min-h-[220px] bg-gray-100 shrink-0">
                        {featuredPreview ? (
                            <img 
                                src={featuredPreview} 
                                className="w-full h-full object-cover" 
                                alt="Featured preview" 
                            />
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                                <i className="fa-solid fa-image text-4xl"></i>
                                <span className="text-[10px] font-bold uppercase tracking-wider">No Image Selected</span>
                            </div>
                        )}
                        
                        {/* Change Image Button Overlay */}
                        <div className="absolute inset-0 bg-black/35 opacity-0 hover:opacity-100 flex items-center justify-center transition-all duration-300">
                            <button 
                                type="button" 
                                onClick={triggerFileSelect}
                                className="px-4 py-2 bg-white/90 text-gray-800 rounded-xl text-xs font-bold shadow-md hover:bg-white flex items-center gap-2 active:scale-95 transition-all"
                            >
                                <i className="fa-solid fa-camera"></i> Change Image
                            </button>
                        </div>
                        {/* Quick Action Button visible even when overlay is not hovered */}
                        <button
                            type="button"
                            onClick={triggerFileSelect}
                            className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-md text-white rounded-lg text-[10px] font-bold flex items-center gap-1.5 shadow-md"
                        >
                            <i className="fa-solid fa-camera"></i> Change Image
                        </button>
                    </div>

                    {/* Right: Card Details */}
                    <div className="flex-1 p-6 flex flex-col justify-between space-y-4">
                        <div>
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h4 className="text-xl font-bold text-gray-900 leading-tight">
                                    {form.name || 'Project Name'}
                                </h4>
                                <span className="px-2.5 py-1 text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-100 rounded-full uppercase">
                                    {form.listingType || 'For Sale'}
                                </span>
                            </div>

                            {/* Details Icons */}
                            <div className="flex items-center gap-6 text-xs text-gray-500 font-semibold mt-3">
                                <span className="flex items-center gap-1.5">
                                    <i className="fa-solid fa-building text-gray-400"></i>
                                    {form.propertyType.join(', ') || 'Residential'}
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <i className="fa-solid fa-bed text-gray-400"></i>
                                    {form.overviewConfiguration || '3 & 4 BHK'}
                                </span>
                                <span className="flex items-center gap-1.5 text-emerald-600 font-bold">
                                    ₹ {form.overviewPriceRange || '1.24 Cr onwards'}
                                </span>
                            </div>
                        </div>

                        {/* Pills Box */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-3 border-t border-gray-50">
                            <div className="bg-blue-50/70 border border-blue-100/50 rounded-xl p-2.5 flex flex-col">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Location</span>
                                <span className="text-xs font-bold text-blue-900 truncate">
                                    {form.location || form.city || 'Jagatpur, Ahmedabad'}
                                </span>
                            </div>
                            <div className="bg-purple-50/70 border border-purple-100/50 rounded-xl p-2.5 flex flex-col">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Project Type</span>
                                <span className="text-xs font-bold text-purple-900 truncate">
                                    {form.propertyType.join(', ') || 'Residential'}
                                </span>
                            </div>
                            <div className="bg-emerald-50/70 border border-emerald-100/50 rounded-xl p-2.5 flex flex-col">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Configuration</span>
                                <span className="text-xs font-bold text-emerald-900 truncate">
                                    {form.overviewConfiguration || '3 & 4 BHK'}
                                </span>
                            </div>
                            <div className="bg-amber-50/70 border border-amber-100/50 rounded-xl p-2.5 flex flex-col">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Status</span>
                                <span className="text-xs font-bold text-amber-900 truncate">
                                    {form.projectStatus || 'Under Construction'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step1BasicInfo;
